
import React, { createContext, useContext, useState, useEffect } from 'react';
import { logDebug } from '@/utils/logging';
import { useIsMobile } from '@/hooks/use-mobile';

interface VoiceChatContextType {
  isVoiceModeEnabled: boolean;
  hasUserInteracted: boolean;
  autoPlayResponses: boolean;
  selectedVoice: string;
  isSpeaking: boolean;
  voiceModeEnabledTimestamp: number | null;
  toggleVoiceMode: () => void;
  markUserInteraction: () => void;
  setIsSpeaking: (speaking: boolean) => void;
}

const VoiceChatContext = createContext<VoiceChatContextType | undefined>(undefined);

export const useVoiceChat = () => {
  const context = useContext(VoiceChatContext);
  if (context === undefined) {
    throw new Error('useVoiceChat must be used within a VoiceChatProvider');
  }
  return context;
};

export const VoiceChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isMobile = useIsMobile();
  
  // Voice mode state - OFF by default
  const [isVoiceModeEnabled, setIsVoiceModeEnabled] = useState<boolean>(() => {
    try {
      // Clear any existing value to ensure it starts off
      localStorage.removeItem('memory-voice-mode-enabled');
      return false;
    } catch (e) {
      return false;
    }
  });
  
  // Track when voice mode was enabled (to prevent playing old messages)
  const [voiceModeEnabledTimestamp, setVoiceModeEnabledTimestamp] = useState<number | null>(null);
  
  // Track user interaction across the application
  // This is crucial for audio playback on mobile
  const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(() => {
    try {
      // Try multiple storage options for persistence and redundancy
      const sessionValue = sessionStorage.getItem('memory-user-interaction');
      const localValue = localStorage.getItem('memory-user-interaction');
      
      return (sessionValue !== null && JSON.parse(sessionValue)) || 
             (localValue !== null && JSON.parse(localValue)) || 
             false;
    } catch (e) {
      return false;
    }
  });

  // Track if TTS is currently speaking
  const [isSpeaking, setIsSpeaking] = useState(false);

  // Default voice setting
  const selectedVoice = "fable";
  
  // Auto-play setting - always true when voice mode is enabled
  const autoPlayResponses = true;

  // Persist voice mode setting to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('memory-voice-mode-enabled', JSON.stringify(isVoiceModeEnabled));
      
      // When voice mode changes, log the state for debugging
      logDebug('Voice mode state changed', { 
        isVoiceModeEnabled, 
        voiceModeEnabledTimestamp,
        hasUserInteracted
      });
    } catch (e) {
      // Ignore storage errors
    }
  }, [isVoiceModeEnabled, voiceModeEnabledTimestamp, hasUserInteracted]);

  // Persist user interaction state
  useEffect(() => {
    try {
      // Store in both session and local storage for redundancy
      sessionStorage.setItem('memory-user-interaction', JSON.stringify(hasUserInteracted));
      localStorage.setItem('memory-user-interaction', JSON.stringify(hasUserInteracted));
      
      if (hasUserInteracted) {
        logDebug('User interaction state updated and stored', { isMobile });
      }
    } catch (e) {
      // Ignore storage errors
    }
  }, [hasUserInteracted, isMobile]);

  // Set up global interaction detection with enhanced mobile support
  useEffect(() => {
    // Function to handle user interaction
    const handleUserInteraction = () => {
      if (!hasUserInteracted) {
        logDebug('First user interaction detected', { isMobile });
        setHasUserInteracted(true);
      }
    };

    // User interaction events to listen for
    // Add more events that are particularly relevant on mobile
    const interactionEvents = [
      'click', 'touchstart', 'touchend', 'mousedown', 'keydown',
      // Additional events that especially help on mobile
      'pointerdown', 'mouseup', 'touchmove', 'focus', 'scroll'
    ];
    
    // Add listeners
    interactionEvents.forEach(event => {
      document.addEventListener(event, handleUserInteraction, { passive: true });
    });
    
    // Special handling for iOS
    if (isMobile) {
      // iOS often needs a special bootstrap
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          // When the page becomes visible again, check for interaction
          setTimeout(handleUserInteraction, 300);
        }
      };
      
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      // iOS might need a more direct approach
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
      if (isIOS) {
        // On iOS, we try to trigger audio once early
        const initAudio = () => {
          const silentAudio = new Audio('data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAABAAADQgD///////////////////////////////////////////////////////////////////8AAAA5TEFNRTMuMTAwAc0AAAAAAAAAABSAJAJAQgAAgAAAA0JbYAkAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA//sQZAAP8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAETEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVU=');
          silentAudio.load();
          
          const playPromise = silentAudio.play();
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                logDebug('iOS silent audio initialized');
                silentAudio.pause();
                handleUserInteraction();
              })
              .catch(() => {
                // Expected to fail on first load - that's okay
              });
          }
        };
        
        // Try initialization after a short delay
        setTimeout(initAudio, 500);
      }
      
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }

    // Clean up
    return () => {
      interactionEvents.forEach(event => {
        document.removeEventListener(event, handleUserInteraction);
      });
    };
  }, [hasUserInteracted, isMobile]);

  // Toggle voice mode
  const toggleVoiceMode = () => {
    const newVoiceModeState = !isVoiceModeEnabled;
    
    // Update the voice mode state first to ensure React state is updated
    setIsVoiceModeEnabled(newVoiceModeState);
    
    // Record timestamp when voice mode is turned ON
    if (newVoiceModeState) {
      // Use Date.now() to ensure we have a consistent timestamp
      const timestamp = Date.now();
      
      // Add a small delay to ensure the timestamp is set after the voice mode
      // This helps with the race condition where a message is sent immediately after
      setTimeout(() => {
        setVoiceModeEnabledTimestamp(timestamp);
        logDebug('Voice mode enabled with timestamp', { timestamp });
      }, 10);
    } else {
      // When turning off, we should clear the timestamp
      setVoiceModeEnabledTimestamp(null);
      logDebug('Voice mode disabled, timestamp cleared');
    }
    
    // Make sure we've registered user interaction
    markUserInteraction();
  };

  // Mark user interaction - used when we need to explicitly track interaction
  const markUserInteraction = () => {
    if (!hasUserInteracted) {
      logDebug('User interaction manually marked', { isMobile });
      setHasUserInteracted(true);
    }
  };

  // Context value
  const value = {
    isVoiceModeEnabled,
    hasUserInteracted,
    autoPlayResponses,
    selectedVoice,
    isSpeaking,
    voiceModeEnabledTimestamp,
    toggleVoiceMode,
    markUserInteraction,
    setIsSpeaking
  };

  return (
    <VoiceChatContext.Provider value={value}>
      {children}
    </VoiceChatContext.Provider>
  );
};
